import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { createStorage } from '@aviata/storage';
import { cloneDeep } from 'lodash-es';
import { flightEntity } from 'freedom-shared';
import type { Airflow, Dict } from '@aviata/platform-types';

import { mapSavedPassenger, getDefaultState } from './lib';
import type {
  BookingState,
  MedicalInsuranceProductsState,
  SavePassengerPayload,
  NormalizedPassenger,
  FlightDelayBooking,
} from './types';

const bookingDataKey = 'freedom-avia:booking-data';
const bookingDataStorage = createStorage(sessionStorage);

export const useBookingStore = defineStore('booking', () => {
  const offer = ref<Airflow.Offer.Details['offer'] | null>(null);
  const passengersSchema = ref<Airflow.Offer.Schema.Details | null>(null);
  const dict = ref<Dict.Entity | null>();

  const passengersSaveResponse = ref<NormalizedPassenger[] | null>(null);
  const passengersSavePayload = ref<SavePassengerPayload | null>(null);

  const rawData = bookingDataStorage.parse(bookingDataKey);
  const defaultState = getDefaultState(rawData);
  const state = ref(defaultState);

  const mappedFlights = ref<flightEntity.types.MappedFlight[]>([]);

  /**
   * Departure datetime of the last flight in the journey (the return leg on a
   * round-trip, the final leg on a multi-city). Age brackets must be validated
   * against the latest travel date so a passenger who ages into the next tier
   * during the trip (e.g. a child turning 12 on the return date) is classified
   * correctly. Undefined until flights are mapped.
   */
  const lastFlightDepartureDate = computed(
    () =>
      mappedFlights.value[mappedFlights.value.length - 1]?.segments?.[0]?.dep
        ?.at,
  );

  const medicalInsuranceProducts = ref<MedicalInsuranceProductsState[]>([]);
  const flightDelayProducts = ref<FlightDelayBooking[] | null>(null);

  /**
   * Per-offer fare-family selection persisted across QuickBookingPage
   * remounts, so navigation away to seat-map / baggage and back keeps the
   * user's tariff choice. Map is keyed by `groupKey` and stores the chosen
   * fare's id; reset on offer change.
   */
  const selectedFareIds = ref<Record<string, string> | null>(null);
  /** Offer id the cached `selectedFareIds` belongs to. */
  const selectedFareIdsOfferId = ref<string | null>(null);

  /**
   * Offer id for which the user has explicitly removed the service package on
   * QuickBookingPage. Persisted across QuickBookingPage remounts (Pinia
   * singleton), so navigating away to seat-map / baggage / passengers and back
   * does NOT re-run the aggressive first-package preselect against the user's
   * deliberate opt-out. Holds the offer id while the package is unselected,
   * `null` otherwise — a different offer id therefore reads as "not unselected"
   * automatically. Survives a catalogue refresh (fare / passenger change): the
   * refetch brings new package ids, but the user's removal still stands.
   * Cleared when the user picks a package themselves and when they return to
   * the offer-details screen, i.e. start a fresh booking session for the
   * offer.
   */
  const packageManuallyUnselectedOfferId = ref<string | null>(null);

  /**
   * Offer id for which the user has already dealt with the preselected package
   * (SEARCH-2215): they picked a package themselves, removed one, or confirmed
   * the composition in the pre-booking sheet. While this holds the current
   * offer id the package counts as the user's own choice, so the «included for
   * you» label disappears and the confirmation sheet stops interrupting the
   * booking. Lives in the store, not in a page `ref`, for the same reason as
   * `packageManuallyUnselectedOfferId`: QuickBookingPage remounts on
   * back-navigation from seat-map / baggage, and a fresh `ref(false)` would
   * re-label a package the user already dealt with and re-open the sheet.
   * Cleared on a catalogue refresh (fare / passenger change), unlike the
   * unselect flag next to it: whatever the preselect attaches from the fresh
   * catalogue is our pick again and has to be labelled and confirmed as such.
   * Also cleared on return to the offer-details screen, which starts a fresh
   * booking session.
   */
  const packagePreselectResolvedOfferId = ref<string | null>(null);

  const update = (updatedState: Partial<BookingState>) => {
    state.value = { ...state.value, ...updatedState };
    bookingDataStorage.stringify(bookingDataKey, state.value);
  };

  function init(details: Airflow.Offer.Details) {
    update(defaultState);
    offer.value = details.offer;
    passengersSchema.value = details.schema;
    dict.value = details.dict;

    onUpdateFlights(details);
  }

  function updateMedicalInsuranceProducts(
    products: MedicalInsuranceProductsState[],
  ) {
    medicalInsuranceProducts.value = [...products];
  }

  function updateFlightDelayProducts(bookings: FlightDelayBooking[]) {
    flightDelayProducts.value = [...bookings];
  }

  function nullifyFlightDelayProducts() {
    flightDelayProducts.value = null;
  }

  function setPassengersSaveResponse(response: Airflow.Booking.Passenger[]) {
    passengersSaveResponse.value = response.map(mapSavedPassenger);
  }

  function setPassengersSavePayload(payload: SavePassengerPayload) {
    passengersSavePayload.value = cloneDeep(payload);
  }

  function onUpdateFlights(details: Airflow.Offer.Details) {
    mappedFlights.value = details.offer.flights.map(flight =>
      flightEntity.lib.mapFlight(details.dict, flight),
    );
  }

  function setSelectedFareIds(
    offerId: string,
    ids: Record<string, string> | null,
  ) {
    selectedFareIdsOfferId.value = offerId;
    selectedFareIds.value = ids;
  }

  function resetSelectedFareIds() {
    selectedFareIds.value = null;
    selectedFareIdsOfferId.value = null;
  }

  function setPackageManuallyUnselected(offerId: string) {
    packageManuallyUnselectedOfferId.value = offerId;
  }

  function clearPackageManuallyUnselected() {
    packageManuallyUnselectedOfferId.value = null;
  }

  function isPackageManuallyUnselected(offerId: string) {
    return packageManuallyUnselectedOfferId.value === offerId;
  }

  function setPackagePreselectResolved(offerId: string) {
    packagePreselectResolvedOfferId.value = offerId;
  }

  function clearPackagePreselectResolved() {
    packagePreselectResolvedOfferId.value = null;
  }

  function isPackagePreselectResolved(offerId: string) {
    return packagePreselectResolvedOfferId.value === offerId;
  }

  return {
    state,
    dict,
    passengersSchema,
    offer,
    mappedFlights,
    lastFlightDepartureDate,
    init,
    update,
    updateMedicalInsuranceProducts,
    medicalInsuranceProducts,
    setPassengersSavePayload,
    setPassengersSaveResponse,
    passengersSaveResponse,
    passengersSavePayload,
    onUpdateFlights,
    updateFlightDelayProducts,
    flightDelayProducts,
    nullifyFlightDelayProducts,
    selectedFareIds,
    selectedFareIdsOfferId,
    setSelectedFareIds,
    resetSelectedFareIds,
    packageManuallyUnselectedOfferId,
    setPackageManuallyUnselected,
    clearPackageManuallyUnselected,
    isPackageManuallyUnselected,
    packagePreselectResolvedOfferId,
    setPackagePreselectResolved,
    clearPackagePreselectResolved,
    isPackagePreselectResolved,
  };
});
